import { useEffect, useState } from "react";

/**
 * Waits for a screen's images to finish downloading before it is shown, so a
 * screen never paints with empty image slots. Errors count as done — a broken
 * URL must not hold the screen forever.
 *
 * @param {string[]} urls images the caller is about to render
 * @returns {boolean} true once every URL has loaded (or failed)
 */
const useImagesPreloaded = (urls) => {
  const key = urls.join("|");
  const [readyKey, setReadyKey] = useState("");

  useEffect(() => {
    if (!key) {
      setReadyKey("");
      return undefined;
    }

    let isCancelled = false;
    let remaining = urls.length;
    const markDone = () => {
      if (isCancelled) return;
      remaining -= 1;
      if (remaining === 0) setReadyKey(key);
    };

    // Handlers before `src` so a cached image still reports back.
    const images = urls.map((url) => {
      const image = new Image();
      image.onload = markDone;
      image.onerror = markDone;
      image.src = url;
      return image;
    });

    return () => {
      isCancelled = true;
      images.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // No images to wait for is "ready".
  return key === "" || readyKey === key;
};

export default useImagesPreloaded;
